import React from 'react'
import { Box, HStack, FormControl, FormErrorMessage, FormLabel, Button, Icon } from '@chakra-ui/react'
import {
  LabeledInput,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  AutoSuggest
} from '@apex/component-library'
import { FaQuestionCircle } from 'react-icons/fa'
import { AddressFieldProps } from './Types'

export interface AddressFormProps {
  address: AddressFieldProps
  addressLine2: AddressFieldProps
  city: AddressFieldProps
  state: AddressFieldProps
  zip: AddressFieldProps
  country: AddressFieldProps
  handleBlur: (e: React.FocusEvent<any>) => void
  setValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  countryList: { value: string }[]
  countryListLoading: boolean
  countryListFailedMessage?: string
}

export const AddressForm = ({
  address,
  addressLine2,
  city,
  state,
  zip,
  country,
  handleBlur,
  setValue,
  countryList,
  countryListLoading,
  countryListFailedMessage
}: AddressFormProps) => {
  const {
    label: addressLabel,
    name: addressName,
    value: addressValue,
    errors: addressErrors,
    touched: addressTouched
  } = address
  const {
    label: addressLine2Label,
    name: addressLine2Name,
    value: addressLine2Value,
    errors: addressLine2Errors,
    touched: addressLine2Touched
  } = addressLine2
  const { label: cityLabel, name: cityName, value: cityValue, errors: cityErrors, touched: cityTouched } = city
  const { label: stateLabel, name: stateName, value: stateValue, errors: stateErrors, touched: stateTouched } = state
  const { label: zipLabel, name: zipName, value: zipValue, errors: zipErrors, touched: zipTouched } = zip
  const {
    label: countryLabel,
    name: countryName,
    value: countryValue,
    errors: countryErrors,
    touched: countryTouched
  } = country
  return (
    <>
      <Box height='5em'>
        <LabeledInput
          label='Address'
          aria-label={addressLabel}
          name={addressName}
          isRequired
          onBlur={handleBlur}
          value={addressValue}
          onChange={(e) => {
            setValue(addressName, e.target.value)
          }}
          errorMessage={addressErrors}
          isInvalid={addressTouched && !!addressErrors}
        />
      </Box>
      <Box height='5em'>
        <LabeledInput
          label='Address Line 2'
          aria-label={addressLine2Label}
          name={addressLine2Name}
          onBlur={handleBlur}
          value={addressLine2Value}
          onChange={(e) => {
            setValue(addressLine2Name, e.target.value)
          }}
          errorMessage={addressLine2Errors}
          isInvalid={addressLine2Touched && !!addressLine2Errors}
        />
      </Box>
      <HStack spacing={8}>
        <Box height='5em' w='50%'>
          <LabeledInput
            label='City'
            aria-label={cityLabel}
            name={cityName}
            isRequired
            onBlur={handleBlur}
            value={cityValue}
            onChange={(e) => {
              setValue(cityName, e.target.value)
            }}
            errorMessage={cityErrors}
            isInvalid={cityTouched && !!cityErrors}
          />
        </Box>
        <Box height='5em' w='50%'>
          <FormControl isInvalid={!!stateErrors}>
            <FormLabel color={!!stateErrors ? 'red.500' : 'black'}>
              State / Province{' '}
              <Popover
                placement='top-start'
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [-5, 10]
                    }
                  }
                ]}
                trigger='click'
                defaultIsOpen={false}
              >
                <PopoverTrigger>
                  <Button
                    aria-label='state popover'
                    variant='link'
                    h='1rem'
                    d='inline-block'
                    minW='1rem'
                    lineHeight={0}
                    border={0}
                    rounded='full'
                  >
                    <Icon as={FaQuestionCircle} color='black'>
                      Trigger Popover
                    </Icon>
                  </Button>
                </PopoverTrigger>
                <PopoverContent color='black'>
                  <PopoverArrow />
                  <PopoverBody>
                    For domestic addresses, state identifiers must use United States Postal Service state abbreviations.
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </FormLabel>
            <Input
              aria-label={stateLabel}
              name={stateName}
              isRequired
              onBlur={handleBlur}
              value={stateValue}
              onChange={(e) => {
                setValue(stateName, e.target.value)
              }}
              isInvalid={stateTouched && !!stateErrors}
            />
            <FormErrorMessage>{stateErrors}</FormErrorMessage>
          </FormControl>
        </Box>
      </HStack>
      <HStack spacing={8}>
        <Box height='5em' w='50%'>
          <LabeledInput
            label='ZIP / Postal Code'
            aria-label={zipLabel}
            name={zipName}
            value={zipValue}
            onBlur={handleBlur}
            onChange={(e) => setValue(zipName, e.target.value)}
            errorMessage={zipErrors}
            isInvalid={zipTouched && !!zipErrors}
          />
        </Box>
        <Box height='5em' w='50%'>
          <AutoSuggest
            label='Country'
            aria-label={countryLabel}
            name={countryName}
            inputValue={countryValue || ''}
            onBlur={handleBlur}
            onChange={(e) => {
              setValue(countryName, e.target.value)
            }}
            dataSet={countryList}
            onSelectItem={(value) => setValue(countryName, value)}
            isDisabled={countryListLoading || !!countryListFailedMessage}
            placeholder={countryListLoading ? 'Loading...' : ''}
            errorMessage={countryListFailedMessage || countryErrors}
            isInvalid={!!countryListFailedMessage || (countryTouched && !!countryErrors)}
            isRequired
          />
        </Box>
      </HStack>
    </>
  )
}
